import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/date";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { Trash2 } from "lucide-react";

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [confirmClear, setConfirmClear] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data || []);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const deleteOne = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteAll = async () => {
    try {
      const { data } = await api.delete("/notifications");
      toast.success(`Deleted ${data.deleted} notification(s)`);
      setConfirmClear(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); setConfirmClear(false); }
  };

  return (
    <div className="space-y-4" data-testid="notifications-page">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Inbox</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Notifications</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="notifications-mark-all"
            onClick={markAllRead}>Mark all read</Button>
          <Button variant="outline" data-testid="notifications-clear-all"
            className="text-rose-700 border-rose-500/30 hover:bg-rose-500/10"
            disabled={items.length === 0}
            onClick={() => setConfirmClear(true)}>
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete all
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card divide-y divide-border">
        {items.length === 0 && <div className="p-6 text-center text-xs text-muted-foreground">No notifications yet.</div>}
        {items.map((n) => {
          const initial = String(n.title || "N")
            .trim()
            .charAt(0)
            .toUpperCase();

          return (
            <div
              key={n.id}
              className={`group flex items-start gap-3 px-4 py-3.5 transition-colors hover:bg-secondary/30 ${
                !n.read ? "bg-primary/[0.06]" : ""
              }`}
              data-testid={`notification-${n.id}`}
            >
              <div
                className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-xs font-bold ${
                  !n.read
                    ? "border-primary/35 bg-primary/10 text-primary"
                    : "border-border bg-secondary/60 text-muted-foreground"
                }`}
                aria-hidden="true"
              >
                {initial}
              </div>

              <Link
                to={n.ticket_id ? `/tickets/${n.ticket_id}` : "#"}
                onClick={() => { if (!n.read) void markRead(n.id); }}
                className="min-w-0 flex-1"
              >
                <div className="flex items-start gap-2">
                  <div
                    className={`truncate text-sm ${
                      !n.read ? "font-semibold text-foreground" : "font-medium"
                    }`}
                  >
                    {n.title}
                  </div>

                  {!n.read && (
                    <span
                      className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                      aria-label="Unread"
                    />
                  )}
                </div>

                {n.body && (
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">
                    {n.body}
                  </div>
                )}

                {n.current_status_label && !n.status_message_refreshed && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Current ticket status: <span className="font-medium text-foreground">{n.current_status_label}</span>
                  </div>
                )}

                <div className="mt-1.5 text-[10px] text-muted-foreground/70">
                  {formatDateTime(n.created_at)}
                </div>
              </Link>

              <button
                type="button"
                data-testid={`notification-delete-${n.id}`}
                onClick={() => deleteOne(n.id)}
                className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-70 transition hover:bg-rose-500/10 hover:text-rose-600 group-hover:opacity-100"
                aria-label="Delete notification"
                title="Delete notification"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={confirmClear}
        onOpenChange={setConfirmClear}
        title="Delete all notifications?"
        description={`This will permanently remove all ${items.length} notification(s). This cannot be undone.`}
        confirmLabel="Delete all"
        destructive
        onConfirm={deleteAll}
        testid="confirm-clear-notifications"
      />
    </div>
  );
}
